import type { EIP1193Provider } from "../hooks/useEIP6963";

/** EIP-1193 code for a request the user dismissed in their wallet. */
const USER_REJECTED = 4001;

/**
 * Phantom rejects EIP-712 requests whose domain chainId is a hex string
 * ("Missing or invalid parameters."), which is how ethers serializes it. As a
 * number it is the same uint256, so the signature, and the privacy keys
 * derived from it, are unchanged.
 */
export function withNumericTypedDataChainId(
  provider: EIP1193Provider,
): EIP1193Provider {
  return {
    request: (args) => {
      const params = Array.isArray(args.params) ? args.params : null;
      if (
        args.method === "eth_signTypedData_v4" &&
        params &&
        typeof params[1] === "string"
      ) {
        let payload: { domain?: { chainId?: unknown } } | null = null;
        try {
          payload = JSON.parse(params[1]);
        } catch {
          /* Not JSON: the wallet sees the request unchanged. */
        }
        const chainId = payload?.domain?.chainId;
        if (
          payload?.domain &&
          typeof chainId === "string" &&
          /^0x[0-9a-f]+$/i.test(chainId) &&
          BigInt(chainId) <= BigInt(Number.MAX_SAFE_INTEGER)
        ) {
          payload.domain.chainId = Number(BigInt(chainId));
          return provider.request({
            method: args.method,
            params: [params[0], JSON.stringify(payload)],
          });
        }
      }
      return provider.request(args);
    },
    on: provider.on?.bind(provider),
    removeListener: provider.removeListener?.bind(provider),
  };
}

/** The wallet's own words for a failure, without ethers' request dump. */
export function walletErrorMessage(error: unknown): string {
  const e = error as {
    error?: { message?: unknown };
    info?: { error?: { message?: unknown } };
    shortMessage?: unknown;
    message?: unknown;
  } | null;
  const message = [
    e?.error?.message,
    e?.info?.error?.message,
    e?.shortMessage,
    e?.message,
  ].find(
    (value): value is string => typeof value === "string" && !!value.trim(),
  );
  if (!message) return "Wallet connection failed";
  return message.length > 240 ? `${message.slice(0, 239)}…` : message;
}

export function isPhantomWallet(
  provider: unknown,
  walletName?: string,
): boolean {
  return (
    walletName === "Phantom" ||
    (provider as { isPhantom?: unknown } | null)?.isPhantom === true
  );
}

/** The user dismissed the request; nothing went wrong to report. */
export function isUserRejection(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return code === USER_REJECTED || code === "ACTION_REJECTED";
}

/**
 * Phantom's EVM wallet only reaches networks it lists itself: Base Sepolia
 * once Testnet Mode is on, and no custom networks such as DogeOS Chikyu.
 */
export function phantomNetworkMessage(
  chainId: number,
  networkLabel: string,
): string {
  return chainId === 84532
    ? "Phantom couldn't switch to Base Sepolia. In Phantom, turn on Testnet Mode (Settings → Developer Settings), then connect again."
    : `Phantom doesn't support ${networkLabel}. Connect with MetaMask or another EVM wallet that can add custom networks.`;
}
